import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Download, RefreshCw, FileText } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";

export const downloadBlob = async (url, filename) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = filename;
    a.click();
  } catch (e) { toast.error("Download fehlgeschlagen"); }
};

export default function TraegerTemplatesTab() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ title: "", purpose: "vertrag", file: null });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceId, setReplaceId] = useState(null);
  const load = () => api.get("/traeger-center/templates").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  const send = async (fd) => {
    setBusy(true);
    try { await api.post("/traeger-center/templates", fd, { timeout: 60000 }); toast.success("Vorlage gespeichert"); load(); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); return false; }
    finally { setBusy(false); }
  };
  const upload = async () => {
    if (!f.title.trim() || !f.file) return toast.error("Bitte Titel und Datei angeben");
    const fd = new FormData();
    fd.append("title", f.title); fd.append("purpose", f.purpose); fd.append("file", f.file);
    if (await send(fd)) { setF({ title: "", purpose: f.purpose, file: null }); if (fileRef.current) fileRef.current.value = ""; }
  };
  const replace = async (file) => {
    const t = d.items.find((x) => x.template_id === replaceId);
    if (!file || !t) return;
    const fd = new FormData();
    fd.append("title", t.title); fd.append("purpose", t.purpose); fd.append("file", file); fd.append("template_id", t.template_id);
    await send(fd);
    replaceRef.current.value = "";
  };
  const remove = async (t) => {
    if (!window.confirm(`Vorlage „${t.title}" löschen?`)) return;
    await api.delete(`/traeger-center/templates/${t.template_id}`);
    load();
  };
  if (!d) return <div className="text-xs text-slate-400">Lädt…</div>;
  return (
    <div className="space-y-6" data-testid="traeger-templates">
      <div className="border border-slate-200 bg-white p-5 grid grid-cols-1 md:grid-cols-[1fr_200px_1fr_auto] gap-3 items-end">
        <label className="text-xs text-slate-600 flex flex-col">Titel<input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="input-tech mt-1" placeholder="z. B. Teilnehmervertrag AVGS" data-testid="tpl-title" /></label>
        <label className="text-xs text-slate-600 flex flex-col">Verwendungszweck
          <select value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} className="input-tech mt-1" data-testid="tpl-purpose">
            {Object.entries(d.purposes).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <div className="text-xs text-slate-600 flex flex-col">Datei (PDF, DOCX, PNG, JPG)
          <input ref={fileRef} type="file" accept=".pdf,.docx,.png,.jpg,.jpeg" className="hidden" onChange={(e) => setF({ ...f, file: e.target.files[0] || null })} data-testid="tpl-file" />
          <button type="button" onClick={() => fileRef.current?.click()} className="input-tech mt-1 text-left truncate" data-testid="tpl-file-pick">{f.file ? f.file.name : <span className="text-slate-400">Datei auswählen…</span>}</button>
        </div>
        <button onClick={upload} disabled={busy} className="px-4 py-2.5 text-xs font-medium bg-slate-900 text-white hover:bg-black disabled:opacity-50 flex items-center gap-1.5 justify-center" data-testid="tpl-upload"><Upload size={12} /> Hochladen</button>
      </div>
      <input ref={replaceRef} type="file" accept=".pdf,.docx,.png,.jpg,.jpeg" className="hidden" onChange={(e) => replace(e.target.files[0])} data-testid="tpl-replace-file" />
      <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="tpl-list">
        {d.items.length === 0 && <div className="p-8 text-center text-sm text-slate-500">Noch keine Vorlagen hochgeladen.</div>}
        {d.items.map((t) => (
          <div key={t.template_id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`tpl-row-${t.template_id}`}>
            <FileText size={16} className="text-slate-400" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-900">{t.title} <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600">{d.purposes[t.purpose]}</span></div>
              <div className="text-[11px] font-mono text-slate-400">{t.filename} · Version {t.version} · {formatDeShort(t.updated_at)}</div>
            </div>
            <button onClick={() => downloadBlob(`/traeger-center/templates/${t.template_id}/file`, t.filename)} className="text-slate-500 hover:text-slate-900" title="Herunterladen" data-testid={`tpl-download-${t.template_id}`}><Download size={14} /></button>
            <button onClick={() => { setReplaceId(t.template_id); replaceRef.current?.click(); }} className="text-slate-500 hover:text-slate-900" title="Ersetzen" data-testid={`tpl-replace-${t.template_id}`}><RefreshCw size={14} /></button>
            <button onClick={() => remove(t)} className="text-slate-400 hover:text-red-600" title="Löschen" data-testid={`tpl-delete-${t.template_id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <div className="border border-slate-200 bg-white" data-testid="tpl-traeger-forms">
        <div className="px-5 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Trägerbestätigungs-Vordrucke aus den Teilnehmerakten (neueste 30)</div>
        {d.traeger_forms.length === 0 ? <div className="p-5 text-sm text-slate-400">Noch keine Vordrucke hochgeladen.</div> : (
          <ul className="divide-y divide-slate-100">{d.traeger_forms.map((x) => (
            <li key={x.form_id} className="px-5 py-2.5 text-sm flex items-center gap-3">
              <span className="flex-1 truncate">{x.filename}</span>
              <span className="text-[11px] text-slate-400">{x.kind === "client" ? "AVGS" : "Weiterbildung"} · {formatDeShort(x.created_at)}</span>
              <a href={x.kind === "client" ? `/avgs/${x.person_id}?tab=unterlagen` : `/weiterbildung/kunden/${x.person_id}?tab=unterlagen`} className="text-xs underline text-slate-600">Akte</a>
            </li>))}
          </ul>)}
      </div>
    </div>
  );
}
